import Axios from 'axios';
import { dynamicParameter } from './service/mixin-service';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import { mergeData } from '@/utils/merge-data-util';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import { parseFilters, parseSort } from '../services/parse-filters-service';

const defaultGetData = () => response => response.data.data.map(item => item.data);
const getTotalHits = response => response.data.totalHits;
const getMaxTotalHits = response => response.data.maxTotalHits;
const getAggregations = response => response.data.aggregations;

const defaultParams = () => { };

const defaultIsRestoreInitialValue = () => false
const defaultNoLoadingWithoutInput = () => false

export default function lazyLoading(path, params = defaultParams, itemsPerLoad = 50, getData = defaultGetData, invokedParamHandler = null, isLazyBaseLevelTableModule = false, customLoad = undefined, isRestoreInitialValue = defaultIsRestoreInitialValue, handleAfterReload = undefined, noLoadingWithoutInput = defaultNoLoadingWithoutInput, loadTotalHits = false) {
  const getPath = dynamicParameter(path);
  const getParams = dynamicParameter(params);
  const getItemsPerLoad = dynamicParameter(itemsPerLoad);
  const getGetData = dynamicParameter(getData);
  const getInvokedParamHandler = dynamicParameter(invokedParamHandler);
  const getIsLazyBaseLevelTableModuleComponent = dynamicParameter(isLazyBaseLevelTableModule);
  const getCustomLoad = dynamicParameter(customLoad);
  const getIsRestoreInitialValue = dynamicParameter(isRestoreInitialValue);
  const getHandleAfterReload = dynamicParameter(handleAfterReload);
  const getNoLoadingWithoutInput = dynamicParameter(noLoadingWithoutInput);
  const getLoadTotalHits = dynamicParameter(loadTotalHits);

  return {
    data() {
      return {
        aggregations: {},
        totalHits: 0,
        noNewResults: false,
        lazyItems: [],
        selectedItems: [],
        isLoading: false,
        isPending: false,
        hasQueuedRequest: false,
        handleParamsChanged: function () {
          this.noNewResults = false;
          if (!this.isLoading)
            this.reload(true);
          else this.hasQueuedRequest = true;
        },
        handleParamsChangedDebounce: debounce(function () {
          this.noNewResults = false;
          if (!this.isLoading)
            this.reload(true);
          else this.hasQueuedRequest = true;
        }, 300)
      }
    },
    methods: {
      load(params, page, size, applyPending = true, applyLoading = true) {
        this.isLoading = applyLoading;

        if (getCustomLoad(this) !== undefined) {
          this.isPending = applyPending;
          return getCustomLoad(this)(params, page, size)
            .then(response => { return response })
            .finally(() => {
              this.isLoading = false;
              this.isPending = false;
            })
        }

        if (getInvokedParamHandler(this) !== undefined && typeof getInvokedParamHandler(this) === 'function') {
          const { parsedParams } = getInvokedParamHandler(this)(params);
          params = parsedParams;
        }

        let { sort, sortDesc, search, mandatoryFilters, ...parsedParams } = params;
        parsedParams = parseFilters(parsedParams, mandatoryFilters);
        parsedParams.sort = parseSort(sort, sortDesc);
        if (!isEmpty(search)) parsedParams.search = search;

        if (!parsedParams.excludeIds || parsedParams.excludeIds == "")
          delete parsedParams.excludeIds;

        this.isPending = applyPending;

        return Axios.get(`/rest/v4/${getPath(this)}`, { params: { ...parsedParams, page, size } })
          .then(response => { return response })
          .finally(() => {
            this.isLoading = false;
            this.isPending = false;
          });
      },
      loadNextData() {
        if (!this.isLoading && !this.noNewResults) {
          return this.load(this.$_params, this.$_nextPage, getItemsPerLoad(this))
            .then(response => {
              let result = getGetData(this)(response);
              if (result?.length < getItemsPerLoad(this))
                this.noNewResults = true;
              result.forEach(item => this.lazyItems.push(item));
              return response;
            });
        }
      },
      async reload(withHandleReload = false) {
        if (getNoLoadingWithoutInput(this) || this.dirtyDialogIsOpen)
          return;

        let params = this.$_params;

        if (getInvokedParamHandler(this) !== undefined && typeof getInvokedParamHandler(this) === 'function') {
          const { parsedParams, triggerLoad } = getInvokedParamHandler(this)(params);
          if (triggerLoad === false) { return }
          params = parsedParams;
        }

        if (!this.isLoading) {
          let oldLazyItems = cloneDeep(this.lazyItems);
          let response = await this.load(params, 0, getItemsPerLoad(this));

          this.lazyItems = getGetData(this)(response) ?? [];
          if (this.lazyItems?.length < getItemsPerLoad(this))
            this.noNewResults = true;

          for (var i = 0; i < this.selectedItems.length; i++) {
            if (!this.lazyItems.some((item) => item == this.selectedItems[i])) {
              let mergedData = mergeData(cloneDeep(this.selectedItems[i]), oldLazyItems?.find(old => old.id == this.selectedItems[i].id))
              this.lazyItems = [...this.lazyItems, mergedData];
            }
          }

          if (this.selectedItems?.length < 1) {
            const valueId = isNaN(this.value) ? this.value?.id : this.value;
            if (valueId && !this.lazyItems.some((item) => item?.id == valueId)) {
              let valueData = oldLazyItems?.find(old => old?.id == valueId)
              if (isNaN(this.value))
                valueData = mergeData(cloneDeep(this.value), valueData);
              if (valueData)
                this.lazyItems = new Array(...this.lazyItems, valueData);
            }
          }

          this.aggregations = getAggregations(response);
          if (getLoadTotalHits(this)) {
            this.totalHits = undefined;
            this.maxTotalHits = undefined;
            let totalHitsParams = { requestType: 'COUNT', ...params };
            this.load(totalHitsParams, 0, getItemsPerLoad(this), false, false).then(totalHitsResponse => {
              this.totalHits = getTotalHits(totalHitsResponse);
              this.maxTotalHits = getMaxTotalHits(totalHitsResponse);
            });
          }

          if (this.hasQueuedRequest) {
            this.hasQueuedRequest = false;
            this.reload();
          }
        }
        else this.hasQueuedRequest = true;

        if (withHandleReload === true && handleAfterReload != undefined) getHandleAfterReload(this);
      }
    },
    computed: {
      $_nextPage() {
        if (getIsRestoreInitialValue(this)) {
          return (
            getItemsPerLoad(this) &&
            Math.ceil(((this.lazyItems?.length ?? 0) - (this.selectedItems?.length ?? 0)) / getItemsPerLoad(this))
          );
        }
        return (
          getItemsPerLoad(this) &&
          Math.ceil((this.lazyItems?.length ?? 0) / getItemsPerLoad(this))
        );
      },
      $_params() {
        return getParams(this);
      }
    },
    watch: {
      $_params: {
        handler(newValue, oldValue) {
          if (isEqual(newValue, oldValue))
            return;
          if (getIsLazyBaseLevelTableModuleComponent(this))
            this.handleParamsChanged(newValue, oldValue);
          else
            this.handleParamsChangedDebounce(newValue, oldValue);
        },
        immediate: false,
      }
    }
  }
}

